import type React from 'react';
import { Text } from 'slate';
import Serializer, {
  type SerializeArguments,
  type DeserializeArguments,
  type MarkAttributes,
} from './Serializer';
import { defaultFontList } from '../../plugins/Formatting/FontFamilyPlugin/fontList';
import colorUtils from '../../util/color';
import { isHTMLElementNode } from '../helpers/serializerHelpers';
import { type Marks } from '../../types/aha-editor';

// Find the font in the serializer font list. If not found, fall
// back to converting to _ case.
function convertFontFamilyToMark(fontFamily: string) {
  const fontLowerCase = fontFamily.split(',')[0].toLowerCase();
  const foundFont = defaultFontList.find((font) =>
    font.fontFamily.toLowerCase().startsWith(fontLowerCase)
  );
  if (foundFont) {
    return foundFont.styleName;
  }
  return fontLowerCase.replace(/ /g, '_');
}

export type MarkSerializeArguments = SerializeArguments & {
  markValue: any;
  styles: any;
  otherProps: any;
};

export interface MarkStyles {
  backgroundColor?: string;
  color?: string;
  fontSize?: string;
  fontStyle?: string;
  fontFamily?: string;
  fontWeight?: string;
  textDecoration?: string;
}

// Helper super-class to serialize/deserialze marks. Marks are serialized
// either as tags or styles on a tag or a span. Subclasses SerializerInlineMark
// and SerializerStyle handle these cases.
class MarkSerializer extends Serializer {
  // Map of HTML tags to marks for deserialization. Implement in subclass.
  static deserializeTagsToMarks: { [key: string]: string } = {};
  // Map of marks to function that returns JSX for serialization. Implement in subclass.
  static serializeMarks: {
    [key: string]: (args: MarkSerializeArguments) => React.ReactNode;
  } = {};

  // Convert marks to a style object.
  static marksToStyles(marks: any) {
    const styles: any = {};
    const otherProps: any = {};

    for (const mark of Object.keys(marks)) {
      const markValue = marks[mark];

      switch (mark) {
        case 'highlight':
          styles.backgroundColor = '#FFFF9D';
          break;

        case 'textColor':
          styles.color = markValue;
          break;

        case 'textHighlight':
          styles.backgroundColor = markValue;
          break;

        // TODO: textAlign should be here.

        case 'fontSize':
          styles.fontSize = markValue;
          break;

        case 'fontFamily':
          {
            const fontItem = defaultFontList.find(
              (font) => font.styleName === markValue
            );
            const fontFamily = fontItem
              ? fontItem.fontFamily
              : markValue.split('_').join(' ');

            styles.fontFamily = fontFamily;
            otherProps['data-font-family-mark'] = markValue;
          }
          break;
      }
    }

    return { styles, otherProps };
  }

  // Convert styles to marks.
  static stylesToMarks(el: HTMLElement, options: any): MarkAttributes {
    const newMarks: MarkAttributes = {};

    // Bold
    if (
      el.style.fontWeight === 'bold' ||
      parseInt(el.style.fontWeight, 10) >= 600
    ) {
      newMarks.bold = true;
    }

    // Italic
    if (
      el.style.fontStyle === 'italic' ||
      el.style.fontStyle?.startsWith('oblique')
    ) {
      newMarks.italic = true;
    }

    // Underline
    if (el.style.textDecoration?.match('underline')) {
      newMarks.underline = true;
    }

    // Highlight
    if (el.classList.contains('inline-highlight')) {
      newMarks.highlight = true;
    }

    const hasVariables = (value: string) => value.match(/var\(--[\w-]+\)/);

    // Text color
    if (el.style.color && !hasVariables(el.style.color)) {
      const textColor = colorUtils.normalizeTextColor(el.style.color);
      if (textColor) {
        newMarks.textColor = textColor;
      }
    }

    // Text highlight
    if (el.style.backgroundColor && !hasVariables(el.style.backgroundColor)) {
      const textHighlight = colorUtils.normalizeTextHighlight(
        el.style.backgroundColor
      );

      if (textHighlight) {
        newMarks.textHighlight = textHighlight;
      }
    }

    // Font size
    if (
      options.enableFontSizeMark &&
      el.style.fontSize &&
      !hasVariables(el.style.fontSize)
    ) {
      newMarks.fontSize = el.style.fontSize;
    }

    // Font family
    if (
      options.enableFontFamilyMark &&
      el.style.fontFamily &&
      !hasVariables(el.style.fontFamily)
    ) {
      // We store the mark on a data-font-family-mark attribute. Get from here
      // if available, otherwise convert the fontFamily style
      const markName = el.dataset.fontFamilyMark
        ? el.dataset.fontFamilyMark
        : convertFontFamilyToMark(el.style.fontFamily);
      if (markName) {
        newMarks.fontFamily = markName;
      }
    }

    return newMarks;
  }

  static serialize({
    node,
    path,
    children,
    context,
    options,
  }: SerializeArguments): React.ReactNode {
    if (!Text.isText(node)) return;
    const { text, ...marks } = node;

    const { styles, otherProps } = this.marksToStyles(marks);

    let result: React.ReactNode;
    for (const mark of Object.keys(marks) as (keyof Marks)[]) {
      if (!Object.hasOwn(this.serializeMarks, mark)) continue;

      result = this.serializeMarks[mark]({
        node,
        path,
        children: result ? [result] : children,
        markValue: marks[mark],
        context,
        options,
        styles,
        otherProps,
      });
    }

    return result;
  }

  static deserialize({
    el,
    markAttributes,
    next,
    options,
  }: DeserializeArguments) {
    if (!isHTMLElementNode(el)) return;

    const tagName = el.tagName.toLowerCase();
    if (!Object.hasOwn(this.deserializeTagsToMarks, tagName)) return;

    const markName = this.deserializeTagsToMarks[tagName];

    const newMarks: MarkAttributes = this.stylesToMarks(el, options);

    const childAttributes: MarkAttributes = {
      ...markAttributes,
      ...newMarks,
      [markName]: true,
    };

    const children = next(el.childNodes, childAttributes);
    if (children.length === 0) {
      children.push({
        text: '',
        ...childAttributes,
      });
    }

    return children;
  }
}

export default MarkSerializer;
